// App.js
import './App.css';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Dashboard from './Dashboard';
import LiteDashboard from './components/LiteDashboard';
import DiscordLogin from './DiscordLogin';

const API_BASE = process.env.REACT_APP_API_BASE;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('discord_token'));
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('discord_token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/user-data`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setUserData(response.data);
        setIsAuthenticated(true);
        setApprovalStatus(null);
      } catch (error) {
        console.error('Token validation failed:', error);

        if (error.response?.data?.error === 'pending_approval') {
          setApprovalStatus('pending');
          setIsAuthenticated(false);
        } else if (error.response?.data?.error === 'access_denied') {
          setApprovalStatus('rejected');
          setIsAuthenticated(false);
          localStorage.removeItem('discord_token');
          localStorage.removeItem('discord_code_verifier');
          localStorage.removeItem('discord_dashboard_data');
        } else {
          localStorage.removeItem('discord_token');
          localStorage.removeItem('discord_code_verifier');
          localStorage.removeItem('discord_dashboard_data');
          setIsAuthenticated(false);
          setApprovalStatus(null);
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const handleLogin = async () => {
    setTimeout(async () => {
      const token = localStorage.getItem('discord_token');
      if (token) {
        try {
          const response = await axios.get(`${API_BASE}/api/user-data`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUserData(response.data);
          setIsAuthenticated(true);
          setApprovalStatus(null);
        } catch (error) {
          console.error('Failed to load user data after login:', error);

          if (error.response?.data?.error === 'pending_approval') {
            setApprovalStatus('pending');
            setIsAuthenticated(false);
          } else if (error.response?.data?.error === 'access_denied') {
            setApprovalStatus('rejected');
            setIsAuthenticated(false);
            localStorage.removeItem('discord_token');
            localStorage.removeItem('discord_code_verifier');
            localStorage.removeItem('discord_dashboard_data');
          } else {
            setIsAuthenticated(false);
            setApprovalStatus(null);
            localStorage.removeItem('discord_token');
            localStorage.removeItem('discord_code_verifier');
            localStorage.removeItem('discord_dashboard_data');
          }
        }
      }
    }, 500);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserData(null);
    setApprovalStatus(null);
    localStorage.removeItem('discord_token');
    localStorage.removeItem('discord_code_verifier');
    localStorage.removeItem('discord_dashboard_data');
  };

  const handleSelfApprove = async () => {
    try {
      const token = localStorage.getItem('discord_token');
      const response = await axios.post(`${API_BASE}/api/self-approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message);
      window.location.reload();
    } catch (error) {
      alert('Fehler: ' + (error.response?.data?.error || error.message));
    }
  };

  const renderPendingApproval = () => (
    <div className="hero">
      <div className="hero-badge" style={{ background: '#ffc107', color: '#000' }}>Warte auf Bestätigung</div>
      <h1 className="hero-title">Zugang ausstehend</h1>
      <p className="hero-subtitle" style={{ color: '#ffc107' }}>
        Ihr Zugang muss erst von einem Panel Admin bestätigt werden.
      </p>
      <p className="muted" style={{ marginTop: '20px' }}>
        Bitte wenden Sie sich an einen Administrator, um Ihren Zugang freizuschalten.
      </p>
      <button
        onClick={handleSelfApprove}
        className="btn btn-primary"
        style={{ marginTop: '20px', background: '#28a745', border: 'none', padding: '12px 24px' }}
      >
        🎉 Ich bin Panel Admin - Selbst freischalten
      </button>
      <button
        onClick={handleLogout}
        className="btn btn-outline"
        style={{ marginTop: '20px', marginLeft: '10px', borderColor: '#666', color: '#888' }}
      >
        Abmelden
      </button>
    </div>
  );

  const renderRejected = () => (
    <div className="hero">
      <div className="hero-badge" style={{ background: '#dc3545', color: '#fff' }}>Zugang abgelehnt</div>
      <h1 className="hero-title">Zugriff verweigert</h1>
      <p className="hero-subtitle" style={{ color: '#dc3545' }}>
        Ihr Zugang wurde abgelehnt.
      </p>
      <p className="muted" style={{ marginTop: '20px' }}>
        Bitte kontaktieren Sie einen Administrator, falls Sie Fragen haben.
      </p>
      <button
        onClick={handleLogout}
        className="btn btn-outline"
        style={{ marginTop: '20px', borderColor: '#666', color: '#888' }}
      >
        Abmelden
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="app-shell full-screen center">
        <div className="spinner" />
        <p className="muted">Lädt...</p>
      </div>
    );
  }

  const isLitePanel = window.location.hostname === 'creator.communitypanel.de';

  const renderDashboard = () => {
    if (isLitePanel) {
      return <LiteDashboard userData={userData} handleLogout={handleLogout} />;
    }
    return <Dashboard userData={userData} handleLogout={handleLogout} />;
  };

  return (
    <div className="app-shell">
      <div className="particles-bg" />
      {approvalStatus === 'pending' ? renderPendingApproval() :
        approvalStatus === 'rejected' ? renderRejected() :
          !isAuthenticated ? (
            <div className="hero">
              <div className="hero-badge">Salerno City Network</div>
              <h1 className="hero-title">Salerno City Webpanel</h1>
              <p className="hero-subtitle">Verbinde dich mit dem Dashboard</p>
              <DiscordLogin onLogin={handleLogin} onLogout={handleLogout} />
              <p className="muted">Bitte mit Discord anmelden, um das Dashboard zu sehen.</p>
            </div>
          ) : (
            renderDashboard()
          )}
    </div>
  );
}

export default App;
